import { Component, OnInit, Input, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import * as words from '../../services/language'
import { MessageService } from '../../services/message.service';
import { MasterService } from '../../services/master.service';
import { StoreService } from '../../services/store.service';
import { SecurityService } from '../../services/security.service';
import moment from 'moment';
import { Subject } from "rxjs";
import { ago, daysAgo } from 'src/app/utils/date';
import { Subscription } from 'rxjs';
import { formatPhone } from 'src/app/utils/format'
import { Contact } from 'src/app/models/contact.model';
import { User } from 'src/app/models/user.model';
import { paymentCategoryIcon } from 'src/app/utils/icons';
import { InventorySetup } from 'src/app/models/inventory-setup.model';
import { Receivable } from 'src/app/models/receivable.model';
import { AutomationEntry } from 'src/app/models/automation-entry.model';

@Component({
    selector: 'all-accounts-view',
    templateUrl: './all-accounts-view.component.html',
    styleUrls: ['./all-accounts-view.component.scss'],
    providers: [TitleCasePipe],
    standalone: false
})
export class AllAccountsViewComponent implements OnInit, OnChanges, OnDestroy {

    /** Unique internal identity of the sale receivable whose collections will be shown. */
    @Input() termsId: number = 0

    /** Unique internal identity of the sale whose collections will be shown. */
    @Input() saleId: number = 0

    /** Unique internal identity of the contact/customer whose collections will be shown. */
    @Input() contactId: number = 0

    /** Currently selected lot identity. */
    private lotId: number = 0

    /** The collection setup for the lot, dealer or corporation. */
    public setup: InventorySetup | null = null

    /** Save the language */
    public language: string= 'EN'

    /** Set all words */
    public words = words.language

    // Details of the contact currently being viewed.
    public contact: Contact | null = null

    // The full name of the contact whose payments need to be collected.
    public contactName: string | null = null

    // The primary address of the contact whose payments need to be collected.
    public address: string | null = null

    // The primary email address of the contact whose payments need to be collected.
    public email: string | null = null

    // The primary phone/cell number of the contact whose payments need to be collected.
    public phone: string | null = null

    //
    public records: number = 0

    //
    public page: number = 1

    // Indicates whether the data is currently loading.
    public loading: boolean = false

    // Indicates whether any more data is available
    public exhausted: boolean = false

    // Raw list of payments due soon for the given contact.
    private payments: Array<Receivable> = []

    /**  */
    public pays: Object | null = null

    /** */
    protected form: FormGroup;

    private subscriptions: Subscription[] = []

    /** A list of collectors available to the corporation. */
    protected collectors: User[];

    // Groups of related payments so we can build tabs.
    public groups: Object = {}

    /** The current receivable being written off or repossessed. */
    protected receivable: Receivable;
    
    // Basic information to recompute a receivable.
    protected entry: AutomationEntry | null = null

    // To recompute a specific term (more comprehensive) set this identity.
    protected entryTermsId: number = 0

    get groupKeys() { return Object.keys(this.groups) }

    get pastDue() { 
        if (!this.pays || !this.pays['past'] || !this.pays['past']['total']) {
            return 0
        }

        return parseFloat(this.pays['past']['total'])
    }

    get pastDueSince() {
        if (!this.pays || !this.pays['past'] || !this.pays['past']['items'] || (this.pays['past']['items'].length <= 0)) {
            return 'as of today'
        }

        return `since ${moment(this.pays['past']['items'][0].due).format('ddd MMM Do, YYYY')}`
    }

    get pastDueCategories() {
        if (!this.pays || !this.pays['past'] || !this.pays['past']['categories'] || (this.pays['past']['categories'].length <= 0)) {
            return ''
        }

        return `${this.pays['past']['categories'].join(', ')}`
    }

    get dueToday() { 
        if (!this.pays || !this.pays['today'] || !this.pays['today']['total']) {
            return 0
        }

        return parseFloat(this.pays['today']['total'])
    }

    get formattedPhone() {
        if (!this.phone) {
            return null
        }

        return formatPhone(this.phone)
    }

    constructor(private route: ActivatedRoute, private ms: MessageService, private master: MasterService, private store: StoreService, protected security: SecurityService, private router: Router, private fb: FormBuilder, private titleCasePipe: TitleCasePipe) { 
        this.form = this.fb.group({
            collectorId: new FormControl<number>(0, [])
        })
    }

    ngOnInit() {
        if (localStorage.getItem('lot')) {
            this.lotId = parseInt(localStorage.getItem('lot'))
            this.readSetup()
        }

        this.subscriptions.push(
            this.form.get('collectorId').valueChanges.subscribe((_) => {
                this.saveCollector()
            })
        )

        this.fetchCollectors()
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.lotId) {
            this.setup = null
            this.readSetup()
            this.reset()
            this.fetch()
        }

        if (changes.contactId) {
            this.contact = null
            this.contactName = null
            this.address = null
            this.email = null
            this.phone = null

            this.readContact()
            this.reset()
            this.fetch()
        }

        if (changes.saleId) {
            this.reset()
            this.fetch()
        }
    }

    private reset = (): void => {
        this.page = 1
        this.exhausted = false
        this.records = 0
    }

    // Listen to output of navbar
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'changeLot':
                this.lotId = parseInt(e['lot'])
                this.readSetup()
                break
        }
    }

    private readContact = (): void => {        
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }
        
        this.master.get(`contacts/${this.contactId}`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.contact) {
                return
            }
            
            this.contact = res.data.contact
            this.contactName = this.contact.name
            this.determineAddress(this.contact.contactAddresses)
            this.determineEmail(this.contact.contactCommunications)
            this.determinePhone(this.contact.contactCommunications)
        })
    }

    private determineAddress = (addresses) => {
        this.address = null
        if (!addresses || (addresses.length <= 0)) {
            return
        }

        const primary = addresses.find((addy) => addy.primaryAddress)
        if (!primary) {
            return
        }

        this.address = `${primary.address} ${this.titleCasePipe.transform(primary.city)}, ${primary.state} ${primary.zipCode}`
    }

    private determineEmail = (communications) => {
        this.email = null
        if (!communications || (communications.length <= 0)) {
            return
        }

        const primary = communications.find((comm) => ((comm.type === 'email') && comm.primary))
        if (!primary) {
            return
        }

        this.email = `${primary.value}`
    }

    private determinePhone = (communications) => {
        this.phone = null
        if (!communications || (communications.length <= 0)) {
            return
        }

        const primary = communications.find((comm) => ((comm.type === 'phone') && comm.primary))
        if (!primary) {
            return
        }

        this.phone = `${primary.value}`
    }

    /**
    * Read the inventory setup for the lot, 
    * dealership or corp (what ever is available).
    */
    private readSetup = async () => {
        if (this.setup) {
            return
        }

        if (!this.lotId || (this.lotId <= 0)) {
            return
        }

        await this.master.getAsync(`lots/${this.lotId}/inventorySetup`).then(async (res) => {
            if (!res || !res.data || !res.data.inventorySetup) {
                this.setup = null
                return
            }

            this.setup = res.data.inventorySetup
        })
    }

    /** 
     * Fetch the the pending payments for the current contact.
    */
    private fetch = (): void => {
        if (this.loading) {
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        if (!this.termsId && (this.termsId <= 0)) {
            this.payments = []
            this.groups = {}
            this.pays = null
            return
        }

        this.loading = true
        if (this.page === 1) {
            this.payments = []
            this.groups = {}
            this.pays = null
        }
        
        this.master.get(`receivables/${this.termsId}`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
  
            this.records = 1
            this.exhausted = true
  
            this.payments = [res.data]
            this.constructGroups()
        })
    }

    /**
     * Groups payments into their respective categories and 
     * loans for the tabbed display in the UI.
     */
    private constructGroups = () => {
        if (!this.payments || (this.payments.length <= 0)) {
            this.groups = {}
            return
        }
        
        if (this.page <= 1) {
            this.groups = {}
        }

        const pays = {
            past: {
                total: 0,
                items: [],
                categories: []
            },
            today: {
                total: 0,
                items: []
            },
            next: {
                total: 0,
                items: []
            }
        }

        for (const payment of this.payments) {
            const key = `${payment.saleId || 0}-${payment.serviceId || 0}-${payment.insuranceId || 0}`
            if (!this.groups[key]) {
                this.groups[key] = {}
                this.groups[key].id = payment.id
                this.groups[key].payments = []
                this.groups[key].termsId = payment.termsId
                this.groups[key].terms = null
                this.groups[key].collectorId = payment.collectorId
                this.groups[key].contactId = payment.contactId
                this.groups[key].url = payment.url
                this.groups[key].vehicleId = payment.vehicleId
                this.groups[key].title = `${payment.contact}`
                this.groups[key].subtitle = ''
                this.groups[key].category = payment.category
                this.groups[key].method = payment.method
                this.groups[key].sold = payment.sold
                this.groups[key].lienholder = payment.lienholder
                this.groups[key].payoff = payment.payoff
                this.groups[key].balance = payment.balance
                this.groups[key].paymentBalance = payment.paymentBalance
                this.groups[key].tax = payment.taxBalance
                this.groups[key].taxMethod = ((payment.taxMethod === 'DEFERRED') ? "Tax Deferred" : 'Tax Paid'),
                this.groups[key].apr = payment.apr
                this.groups[key].payment = payment.payment
                this.groups[key].unearnedInterest = payment.unearnedInterest
                this.groups[key].unpaidEarnedInterest = payment.unpaidEarnedInterest
                this.groups[key].saleId = payment.saleId
                this.groups[key].type = payment.type
                this.groups[key].financed = (payment.financed == true)
                this.groups[key].bhph = (payment.bhph == true)
                this.groups[key].quote = (payment.quote == true)
                this.groups[key].ridingCash = payment.ridingCash
                this.groups[key].lateFees = payment.lateFees
                this.groups[key].gpsCount = payment.gpsCount
                this.groups[key].cpiEnds = payment.cpiEnds
                this.groups[key].insuranceProvider = payment.insuranceProvider
                this.groups[key].insuranceExpires = payment.insuranceExpires
                this.groups[key].frequency = payment.frequency
                this.groups[key].prevPaymentDate = payment.prevPaymentDate
                this.groups[key].percentPrincipalPaid = payment.percentPrincipalPaid

                if (payment.vin) {
                    this.groups[key].title = `${payment.vehicle}`
                    this.groups[key].subtitle = `${payment.stock} | ${payment.vin}`
                }

                this.form.get('collectorId').setValue(payment.collectorId || 0, {emitEvent: false})
            }

            if (payment.deposit > 0) {
                this.groups[key].payments.push({
                    id: payment.saleId,
                    termsId: payment.termsId,
                    due: payment.depositDue,
                    category: 'deposit',
                    amount: payment.deposit,
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (payment.initial > 0) {
                this.groups[key].payments.push({
                    id: payment.saleId,
                    termsId: payment.termsId,
                    due: payment.initialDue,
                    category: 'initial down',
                    amount: payment.initial,
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (payment.deferred > 0) {
                this.groups[key].payments.push({
                    id: payment.saleId,
                    termsId: payment.termsId,
                    due: payment.deferredDue,
                    category: 'deferred down',
                    amount: payment.deferred,
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (!payment.quote) {
                this.groups[key].payments.push({
                    id: payment.saleId,
                    termsId: payment.termsId,
                    due: payment.loanDue || payment.due,
                    category: payment.bhph ? 'mv loan' : 'drive-out price',
                    amount: (payment.loan || 0),
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (payment.lateFees > 0) {
                this.groups[key].payments.push({
                    id: payment.saleId,
                    termsId: payment.termsId,
                    due: payment.lateFeesDue,
                    category: 'late fee',
                    amount: payment.lateFees,
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (payment.cpi > 0) {
                this.groups[key].payments.push({
                    id: payment.saleId,
                    termsId: payment.termsId,
                    due: payment.cpiDue,
                    category: 'cpi',
                    amount: payment.cpi,
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            for (const item of this.groups[key].payments) {
                const timeline = ago(item.due)
                const cloned = JSON.parse(JSON.stringify(item))

                switch (timeline) {
                    case 'past':
                        pays.past.total += parseFloat(item.amount)
                        pays.past.items.push(cloned)
                        pays.past.categories.push(item.category)

                        pays.today.total += parseFloat(item.amount)
                        pays.today.items.push(cloned)
                        break
                    case 'today':
                        pays.today.total += parseFloat(item.amount)
                        pays.today.items.push(cloned)
                        break
                    default: // soon (next)
                        pays.next.total += parseFloat(item.amount)
                        pays.next.items.push(cloned)
                        break
                }
            }

            this.receivable = payment
        }

        this.pays = pays
    }

    /**
     * Get a list of collectors for the current coproation and lot.
     */
    public fetchCollectors = async () => {
        return await this.master.getAsync(`users/roles/collector`).then(res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.collectors = res.data.users
        })
    }

    /**
     * 
     */
    private saveCollector = async () => {
        const collectorId = parseInt(this.form.get('collectorId').value)
        if (!collectorId || isNaN(collectorId) || (collectorId <= 0)) {
            return
        }

        return await this.master.patch(`sales/${this.saleId}/admin`, {collector: collectorId}, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.ms.sendMessage("alert", { type: "success", text: this.words[this.language]['success'] })
        });
    }

    /**
     * Listen for note events from the notes component then update 
     * the display.
     * @param note The details of the note that was posted.
     */
    public notePosted = (note: Object) => {
        this.reset()
        this.fetch()
    }

    /**
     * Listen for promise events from the promises component then update 
     * the display.
     * @param promise The details of the promise that was posted.
     */
    public promiseAdded = (promise: Object) => {
        this.reset()
        this.fetch()
    }

    protected insuranceMessage = (receivable): string => {
        if (!receivable || !receivable.id) {
            return null
        }

        if (!receivable.insuranceExpires) {
            return 'No insurance'
        }

        if (receivable.insuranceExpires && receivable.insuranceIsCpi) {
            return this.cpiMessage(receivable.insuranceExpires)
        }

        return this.insMessage(receivable.insuranceProvider, receivable.insuranceExpires)
    }

    protected insuranceClass = (receivable): string => {
        if (!receivable || !receivable.id) {
            return null
        }

        if (!receivable.insuranceExpires) {
            return 'danger'
        }

        return this.insClass(receivable.insuranceExpires)
    }

    private cpiMessage = (ends: string): string => {
        if (!ends) {
            return 'No CPI'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 0) {
            return `CPI expired ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        if (days <= 60) {
            return `CPI expires ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        return 'CPI in good standing'
    }

    private insClass = (ends: string): string => {
        if (!ends) {
            return 'secondary'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 30) {
            return 'danger'
        }

        if (days <= 60) {
            return 'warning'
        }

        return 'success'
    }

    private insMessage = (provider: string, ends: string): string => {
        if (!ends) {
            return 'No insurance'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 0) {
            return `Insurance expired ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        if (days <= 60) {
            return `Insurance expires ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        return `${provider} active`
    }

    /**
     * Determine an tab icon to show based on the payment category.
     * @param category The payment category.
     */
    public icon = (category: string) => {
        return paymentCategoryIcon(category)
    }

    /**
     * Open the contact editor in a modal.
     * @param e Event that triggered the contact edit.
     */
    public edit = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        const btn = (document.getElementById('editContactModalBtn_all-accounts') as HTMLButtonElement)
        if (!btn) {
            return
        }

        btn.click()
    }

    /**
     * Contact was modified in the modal.
     * @param {Object} meta Details about the contact modification.
     */
    protected contactModified = (meta: Object) => {
        this.readContact()
    }

    /**
     * Open the write off sale editor in a modal.
     * @param e Event that triggered the write off.
     */
    protected writeOff = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        if (!this.groups || (this.groupKeys.length <= 0)) {
            return
        }

        const receivable = this.groups[this.groupKeys[0]]
        if (!receivable || !receivable.id) {
            return
        }

        const btn = (document.getElementById('btn-write-off-modal_all-accounts') as HTMLButtonElement)
        if (!btn) {
            return
        }

        btn.click()
    }

    /**
     * The sale has been written off by the user.
     * @param meta 
     */
    protected writtenOff = (meta: Object) => {
        this.ms.channelComponents$.emit({ message: 'saleWrittenOff', section: 'accounts', value: meta })

        const btn = (document.getElementById('btn-write-off-modal_all-accounts') as HTMLButtonElement)
        if (!btn) {
            return
        }

        btn.click()
    }

    /**
     * Open the repossess vehicle editor in a modal.
     * @param e Event that triggered the repossession.
     */
    protected repossess = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        if (!this.groups || (this.groupKeys.length <= 0)) {
            return
        }

        const receivable = this.groups[this.groupKeys[0]]
        if (!receivable || !receivable.id) {
            return
        }

        const btn = (document.getElementById('btn-repo-modal_all-accounts') as HTMLButtonElement)
        if (!btn) {
            return
        }

        btn.click()
    }

    /**
     * The sale vehicle has been repossessed by the user.
     * @param meta 
     */
    protected repossessed = (meta: Object) => {
        this.ms.channelComponents$.emit({ message: 'saleRepossesed', section: 'accounts', value: meta })

        const btn = (document.getElementById('btn-repo-modal_all-accounts') as HTMLButtonElement)
        if (!btn) {
            return
        }

        btn.click()
    }

    /**
    *
    */
    protected recompute = (e: Event, termsId: number): void => {
        e.stopPropagation()
        e.preventDefault()

        if (!this.payments || (this.payments.length <= 0)) {
            return
        }

        const receivable = this.payments.find((item) => item.termsId == termsId)
        if (!receivable) {
            return
        }

        const entry = new AutomationEntry()
        entry.lotId = receivable.lotId
        entry.jobType = 'computeTerms'
        entry.corporateId = receivable.corporateId
        entry.asOf = moment(receivable.day).format('YYYY-MM-DD')

        this.entry = entry
        this.entryTermsId = receivable.termsId

        const btn = (document.getElementById("btn-toggle-run-receivable-view") as HTMLButtonElement)
        if (!btn) {
            return
        }

        btn.click()
    }

    /**
     * Indicates the user chose to recompute the receivables and it was successful.
     * @param meta 
     */
    protected recomputed = (meta: Object) => {
        const btn = (document.getElementById("btn-toggle-run-receivable-view") as HTMLButtonElement)
        if (btn) {
            btn.click()
        }

        this.entry = null
        this.entryTermsId = 0

        this.reset()
        this.fetch()
    }

    /**
     * User has chosen to reactivate a closed sale.
     * @param e Event that triggered the reactivate.
     */
    protected reactivate = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        if (!this.receivable) {
            return
        }

        if ((!this.receivable.bhph) || (!['repo', 'write-off', 'paid'].includes(this.receivable.status.toLowerCase()))) {
            return
        }

        this.master.patch(`sales/${this.saleId}/reactivate`, {}, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.ms.channelComponents$.emit({ message: 'saleReactivated', section: 'accounts', value: this.receivable })
            this.reset()
            this.fetch()
        })
    }
}
